/**
 * <s-quick-view> — Inline quick view for product cards
 * open(template, variantId, opener) expands a mini product page directly beneath the opener card's row
 * in its grid (spanning the full section width, with a pointer up to the card). Pressing the same card
 * again, or the close arrow, collapses it; another card moves it. Inside: photo arrows, colour swatches
 * (update price, photo, name, link and the cart line), Add to cart (adds 1, collapses, opens the cart;
 * falls back to the native form post) and a More details link.
 */
class SQuickView extends HTMLElement {
  #ac;
  #contentAc;
  #panel = null;
  #body = null;
  #card = null;
  #opener = null;
  #busy = false;
  #resizeTimer;
  #qvTarget = null;

  connectedCallback() {
    this.#ac = new AbortController();
    window.addEventListener('resize', () => {
      clearTimeout(this.#resizeTimer);
      this.#resizeTimer = setTimeout(() => this.#place(), 150);
    }, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#contentAc?.abort();
    clearTimeout(this.#resizeTimer);
  }

  open(template, variantId, opener) {
    if (!(template instanceof HTMLTemplateElement) || !(opener instanceof HTMLElement)) return false;
    const card = this.#cardFor(opener);
    if (!card) return false;

    if (this.#panel && this.#card === card) {
      this.close();
      return true;
    }
    this.#remove();

    const shell = this.querySelector('template[data-qv-shell]');
    const source = shell instanceof HTMLTemplateElement ? shell.content.firstElementChild : null;
    if (!source) return false;

    const tag = /^(UL|OL)$/.test(card.parentElement?.tagName ?? '') ? 'li' : 'div';
    const panel = document.createElement(tag);
    panel.className = source.className;
    panel.setAttribute('data-qv-panel', '');
    panel.append(...source.cloneNode(true).childNodes);

    this.#panel = panel;
    this.#card = card;
    this.#opener = opener;
    this.#body = panel.querySelector('[data-qv-body]');
    this.#qvTarget = null;
    this.#body?.replaceChildren(template.content.cloneNode(true));

    this.#contentAc = new AbortController();
    this.#bind();
    const start = variantId && this.#body?.querySelector(`[data-qv-swatch][data-variant-id="${CSS.escape(String(variantId))}"]`);
    if (start instanceof HTMLElement) this.#select(start);

    this.#place();
    opener.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => requestAnimationFrame(() => {
      panel.classList.add('qv-open');
      setTimeout(() => panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 460);
    }));
    return true;
  }

  close() {
    const panel = this.#panel;
    if (!panel) return;
    const opener = this.#opener;
    this.#panel = null;
    this.#card = null;
    this.#opener = null;
    this.#contentAc?.abort();
    opener?.setAttribute('aria-expanded', 'false');
    panel.classList.remove('qv-open');
    const done = () => panel.remove();
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) done();
    else setTimeout(done, 470);
    if (opener instanceof HTMLElement && opener.isConnected && panel.contains(document.activeElement)) {
      opener.focus({ preventScroll: true });
    }
  }

  #remove() {
    if (!this.#panel) return;
    this.#contentAc?.abort();
    this.#opener?.setAttribute('aria-expanded', 'false');
    this.#panel.remove();
    this.#panel = null;
    this.#card = null;
    this.#opener = null;
  }

  /** The grid item that holds the opener: the nearest ancestor whose parent lays out as a grid. */
  #cardFor(el) {
    let node = el;
    while (node && node.parentElement && node.parentElement !== document.body) {
      if (getComputedStyle(node.parentElement).display.includes('grid')) return node;
      node = node.parentElement;
    }
    return null;
  }

  /** Put the panel right after the last item in the card's row, and point its arrow at the card. */
  #place() {
    const panel = this.#panel;
    const card = this.#card;
    if (!panel || !card || !card.parentElement) return;
    const grid = card.parentElement;
    const top = card.getBoundingClientRect().top;
    const items = [...grid.children].filter((el) => el !== panel && el.getClientRects().length);
    let last = card;
    for (const item of items) {
      if (Math.abs(item.getBoundingClientRect().top - top) < 4) last = item;
    }
    if (last.nextElementSibling !== panel) last.after(panel);

    const g = grid.getBoundingClientRect();
    const c = card.getBoundingClientRect();
    panel.style.setProperty('--qv-arrow-x', `${Math.round(c.left + c.width / 2 - g.left)}px`);
  }

  #bind() {
    const opts = { signal: this.#contentAc.signal };
    const panel = this.#panel;
    if (!panel || !this.#body) return;
    panel.querySelector('[data-qv-close]')?.addEventListener('click', () => this.close(), opts);
    panel.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    }, opts);
    this.#bindGallery(opts);
    this.#body.querySelectorAll('[data-qv-swatch]').forEach((button) => {
      button.addEventListener('click', () => this.#select(button), opts);
    });
    this.#body.querySelector('[data-qv-form]')?.addEventListener('submit', (e) => {
      if (!window.Shopify?.actions?.updateCart) return;
      e.preventDefault();
      this.#add();
    }, opts);
  }

  #slides() {
    return this.#body ? [...this.#body.querySelectorAll('[data-qv-slide]')] : [];
  }

  // Show a photo with a cross-fade (matches the product page). Loops round at both ends.
  #show(index, direction) {
    const slides = this.#slides();
    if (!slides.length) return;
    const i = ((index % slides.length) + slides.length) % slides.length;
    const current = this.#qvTarget && slides.includes(this.#qvTarget) ? slides.indexOf(this.#qvTarget) : slides.findIndex((slide) => !slide.hidden);
    if (current === i) return;
    const incoming = slides[i];
    this.#qvTarget = incoming;
    const outgoing = current >= 0 ? slides[current] : null;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dir = direction || (current < 0 || i > current ? 1 : -1);
    slides.forEach((slide, n) => { if (n !== i && n !== current) slide.hidden = true; });
    incoming.loading = 'eager';
    incoming.hidden = false;
    if (reduce || !incoming.animate || !outgoing) {
      if (outgoing) outgoing.hidden = true;
      return;
    }
    // Clean cross-fade, the same as the product page: the old photo sits on top and fades away,
    // revealing the new one underneath. No sliding.
    outgoing.style.zIndex = '1';
    const out = outgoing.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 420, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' });
    const done = () => { outgoing.style.zIndex = ''; if (this.#qvTarget !== outgoing) outgoing.hidden = true; };
    out.onfinish = done;
    out.oncancel = done;
  }

  #step(delta) {
    const slides = this.#slides();
    const current = this.#qvTarget && slides.includes(this.#qvTarget) ? slides.indexOf(this.#qvTarget) : slides.findIndex((slide) => !slide.hidden);
    this.#show((current < 0 ? 0 : current) + delta, delta);
  }

  // Photo navigation with no arrow buttons, like the product page.
  // Desktop: the cursor is a navy arrow pointing to whichever half the mouse is on; a click moves that way.
  // Phones and tablets: swipe, or tap the outer fifth of either side.
  static #cursors = {
    next: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='22' height='22' viewBox='0 0 22 22'%3E%3Cpath d='M8 4l7 7-7 7' fill='none' stroke='%2312203F' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") 11 11, e-resize`,
    prev: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='22' height='22' viewBox='0 0 22 22'%3E%3Cpath d='M14 4l-7 7 7 7' fill='none' stroke='%2312203F' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") 11 11, w-resize`,
  };

  #bindGallery(opts) {
    const gallery = this.#body?.querySelector('[data-qv-gallery]');
    if (!gallery || this.#slides().length < 2) return;
    const desktop = window.matchMedia('(min-width: 64rem) and (hover: hover) and (pointer: fine)');
    const directionAt = (clientX) => {
      const box = gallery.getBoundingClientRect();
      const ratio = (clientX - box.left) / (box.width || 1);
      if (desktop.matches) return ratio < 0.5 ? -1 : 1;
      if (ratio <= 0.2) return -1;
      if (ratio >= 0.8) return 1;
      return 0;
    };
    gallery.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse' || !desktop.matches) return;
      gallery.style.cursor = SQuickView.#cursors[directionAt(event.clientX) < 0 ? 'prev' : 'next'];
    }, { ...opts, passive: true });
    gallery.addEventListener('pointerleave', () => { gallery.style.cursor = ''; }, opts);
    gallery.addEventListener('click', (event) => {
      const delta = directionAt(event.clientX);
      if (delta) this.#step(delta);
    }, opts);
    let startX = null;
    let moved = false;
    gallery.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;
      startX = event.touches[0].clientX;
      moved = false;
    }, { ...opts, passive: true });
    gallery.addEventListener('touchend', (event) => {
      if (startX === null) return;
      const dx = event.changedTouches[0].clientX - startX;
      startX = null;
      if (Math.abs(dx) < 40) return;
      moved = true;
      this.#step(dx < 0 ? 1 : -1);
      if (event.cancelable) event.preventDefault();
    }, opts);
    gallery.addEventListener('click', (event) => { if (moved) { moved = false; event.stopImmediatePropagation(); } }, { ...opts, capture: true });
    gallery.style.touchAction = 'pan-y';
  }

  #select(button) {
    if (!this.#body) return;
    const d = button.dataset;
    this.#body.querySelectorAll('[data-qv-swatch]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
    const set = (selector, fn) => {
      const el = this.#body.querySelector(selector);
      if (el) fn(el);
    };
    set('[data-qv-name]', (el) => { el.textContent = d.name ?? ''; });
    set('[data-qv-price]', (el) => { el.textContent = d.price ?? ''; });
    set('[data-qv-compare]', (el) => {
      el.textContent = d.compare ?? '';
      el.hidden = !d.compare;
    });
    set('[data-qv-id]', (el) => { el.value = d.variantId ?? ''; });
    set('[data-qv-link]', (el) => { if (d.url) el.href = d.url; });
    set('[data-qv-error]', (el) => { el.hidden = true; });
    set('[data-qv-add]', (el) => {
      const available = d.available === 'true';
      el.disabled = !available;
      el.textContent = available ? el.dataset.label ?? '' : el.dataset.soldOutLabel ?? '';
    });
    if (d.mediaId) {
      const index = this.#slides().findIndex((slide) => slide.dataset.mediaId === d.mediaId);
      if (index >= 0) this.#show(index);
    }
  }

  async #add() {
    if (this.#busy || !this.#body) return;
    const input = this.#body.querySelector('[data-qv-id]');
    const button = this.#body.querySelector('[data-qv-add]');
    const error = this.#body.querySelector('[data-qv-error]');
    const variantId = input instanceof HTMLInputElement ? input.value.trim() : '';
    if (!variantId || !(button instanceof HTMLButtonElement) || button.disabled) return;

    this.#busy = true;
    button.setAttribute('aria-disabled', 'true');
    if (error instanceof HTMLElement) error.hidden = true;
    let failed = false;
    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [{ merchandiseId: variantId, quantity: 1 }] },
        { event: { context: 'collection' } }
      );
      failed = Boolean(result?.userErrors?.length);
    } catch {
      failed = true;
    }
    button.removeAttribute('aria-disabled');
    this.#busy = false;

    if (failed) {
      if (error instanceof HTMLElement) error.hidden = false;
      return;
    }
    this.close();
    await window.Shopify.actions.openCart?.();
  }
}

if (!customElements.get('s-quick-view')) customElements.define('s-quick-view', SQuickView);
